import React, { useState, useMemo } from 'react';
import styles from './ActivityHeatmap.module.css';

const ActivityHeatmap = ({ data = [] }) => {
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: '' });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Generate available years from data
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i++) {
      years.push(currentYear - i);
    }
    return years;
  }, []);

  // Generate GitHub-style contribution grid
  const { gridData, monthLabels, monthBoundaries, totalApplications } = useMemo(() => {
    const startDate = new Date(selectedYear, 0, 1); // January 1st of selected year
    const endDate = new Date(selectedYear, 11, 31); // December 31st of selected year
    
    // Find the Sunday that starts the week containing startDate
    const startSunday = new Date(startDate);
    startSunday.setDate(startDate.getDate() - startDate.getDay());
    
    // Find the Saturday that ends the week containing endDate
    const endSaturday = new Date(endDate);
    endSaturday.setDate(endDate.getDate() + (6 - endDate.getDay()));
    
    // Create activity map for quick lookup
    const activityMap = {};
    let totalCount = 0;
    data.forEach(item => {
      // Handle both ISO date strings and YYYY-MM-DD format
      const dateKey = item.date.includes('T') ? item.date.split('T')[0] : item.date;
      const itemYear = new Date(dateKey).getFullYear();
      
      if (itemYear === selectedYear) {
        activityMap[dateKey] = (activityMap[dateKey] || 0) + (parseInt(item.activity) || 1);
        totalCount += parseInt(item.activity) || 1;
      }
    });
    
    // Generate all days in the grid
    const allDays = [];
    const currentDate = new Date(startSunday);
    
    while (currentDate <= endSaturday) {
      const dateString = currentDate.toISOString().split('T')[0];
      const activity = activityMap[dateString] || 0;
      const isInYear = currentDate >= startDate && currentDate <= endDate;
      
      allDays.push({
        date: dateString,
        activity,
        dayOfWeek: currentDate.getDay(),
        month: currentDate.getMonth(),
        isCurrentYear: isInYear
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Group into weeks (7 days each)
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
      weeks.push(allDays.slice(i, i + 7));
    }
    
    // Generate month labels and track month boundaries
    const months = [];
    const monthBoundaries = new Set();
    const processedMonths = new Set();
    
    weeks.forEach((week, weekIndex) => {
      const firstDay = week[0];
      const firstDayDate = new Date(firstDay.date);
      const monthKey = `${firstDayDate.getFullYear()}-${firstDayDate.getMonth()}`;
      
      if (!processedMonths.has(monthKey) && firstDay.isCurrentYear) {
        months.push({
          name: firstDayDate.toLocaleDateString('en-US', { month: 'short' }),
          weekIndex,
          month: firstDayDate.getMonth()
        });
        processedMonths.add(monthKey);
        
        // Mark this week as a month boundary (except for the first week)
        if (weekIndex > 0) {
          monthBoundaries.add(weekIndex);
        }
      }
    });
    
    return { 
      gridData: weeks, 
      monthLabels: months, 
      monthBoundaries,
      totalApplications: totalCount
    };
  }, [data, selectedYear]);

  // Calculate intensity levels
  const maxActivity = useMemo(() => {
    const activities = gridData.flat().map(day => day.activity).filter(a => a > 0);
    return Math.max(...activities, 1);
  }, [gridData]);

  const getIntensityLevel = (activity) => {
    if (activity === 0) return 0;
    if (activity === 1) return 1;
    if (activity <= maxActivity * 0.25) return 1;
    if (activity <= maxActivity * 0.5) return 2;
    if (activity <= maxActivity * 0.75) return 3;
    return 4;
  };

  const handleMouseEnter = (event, day) => {
    const rect = event.target.getBoundingClientRect();
    const dateObj = new Date(day.date);
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    let content;
    if (day.activity === 0) {
      content = `No applications on ${formattedDate}`;
    } else if (day.activity === 1) {
      content = `1 application on ${formattedDate}`;
    } else {
      content = `${day.activity} applications on ${formattedDate}`;
    }

    setTooltip({
      show: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      content
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, content: '' });
  };

  const dayLabels = ['', 'Mon', '', 'Wed', '', 'Fri', ''];

  return (
    <div className={styles.heatmap}>
      <div className={styles.heatmapHeader}>
        <div className={styles.totalCount}>
          <strong>{totalApplications}</strong> {totalApplications === 1 ? 'application' : 'applications'} in {selectedYear}
        </div>
      </div>

      <div className={styles.heatmapContainer}>
        <div className={styles.heatmapMain}>
          {/* Month labels */}
          <div className={styles.monthLabels}>
            {monthLabels.map((month, index) => (
              <span 
                key={`${month.name}-${index}`}
                className={styles.monthLabel}
                style={{ 
                  left: `${month.weekIndex * 13}px`
                }}
              >
                {month.name}
              </span>
            ))}
          </div>
          
          {/* Main grid */}
          <div className={styles.heatmapGrid}>
            {/* Day labels */}
            <div className={styles.dayLabels}>
              {dayLabels.map((day, index) => (
                <div key={index} className={styles.dayLabel}>
                  {day}
                </div>
              ))}
            </div>
            
            {/* Contribution grid */}
            <div className={styles.contributionGrid}>
              {gridData.map((week, weekIndex) => (
                <div 
                  key={weekIndex} 
                  className={`${styles.week} ${monthBoundaries.has(weekIndex) ? styles.monthBoundary : ''}`}
                >
                  {week.map((day, dayIndex) => {
                    const intensityLevel = getIntensityLevel(day.activity);
                    
                    return (
                      <div
                        key={`${weekIndex}-${dayIndex}`}
                        className={`${styles.day} ${styles[`level${intensityLevel}`]} ${
                          !day.isCurrentYear ? styles.outOfRange : ''
                        }`}
                        onMouseEnter={(e) => handleMouseEnter(e, day)}
                        onMouseLeave={handleMouseLeave}
                        data-activity={day.activity}
                        data-date={day.date}
                        data-level={intensityLevel}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          
          {/* Legend */}
          <div className={styles.legend}>
            <span className={styles.legendText}>Less</span>
            <div className={styles.legendLevels}>
              {[0, 1, 2, 3, 4].map(level => (
                <div 
                  key={level} 
                  className={`${styles.legendSquare} ${styles[`level${level}`]}`}
                />
              ))}
            </div>
            <span className={styles.legendText}>More</span>
          </div>
        </div>

        {/* Year Selector */}
        <div className={styles.yearSelector}>
          {availableYears.map(year => (
            <button
              key={year}
              className={`${styles.yearButton} ${year === selectedYear ? styles.active : ''}`}
              onClick={() => setSelectedYear(year)}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip.show && (
        <div
          className={styles.tooltip}
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateX(-50%) translateY(-100%)'
          }}
        >
          <div className={styles.tooltipContent}>
            {tooltip.content}
          </div>
          <div className={styles.tooltipArrow} />
        </div>
      )}
    </div>
  );
};

export default ActivityHeatmap;